import React, { useEffect, useState } from "react";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import "./Wallet.css";
import { MdHistory } from "react-icons/md";
import { Modal, WalletHeader } from "../../../components";
import { RiLuggageDepositLine } from "react-icons/ri";
import { BiMoneyWithdraw } from "react-icons/bi";
import { Link } from "react-router-dom";
import { FiArrowDownRight, FiArrowUpRight, FiSettings } from "react-icons/fi";
import { useGlobalContext } from "../../../context/context";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { client } from "../Deposit/Deposit";
import { getWalletInvestmentPortfolio } from "../../../utils/investmentApi";

const investmentMoney = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

const investmentShares = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 4,
});

const accountAmountFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function totalForStatuses(records, statuses) {
  return (Array.isArray(records) ? records : [])
    .filter((record) => record && statuses.includes(record.status))
    .reduce((total, record) => total + (Number(record.amount) || 0), 0);
}

function formatAccountAmount(value, currencySymbol) {
  return `${currencySymbol}${accountAmountFormat.format(Number(value) || 0)}`;
}

function formatInvestmentDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const actions = [
  { label: "Deposit", to: "/deposit", icon: RiLuggageDepositLine, detail: "Add funds" },
  { label: "Withdraw", to: "/withdrawal", icon: BiMoneyWithdraw, detail: "Move funds out" },
  { label: "Settings", to: "/settings", icon: FiSettings, detail: "Wallet preferences" },
  { label: "Transaction", to: "/transactions", icon: MdHistory, detail: "View activity" },
];

const Wallet = () => {
  const [message, setMessage] = useState([]);
  const [investmentActivity, setInvestmentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState("");
  const [activityRetry, setActivityRetry] = useState(0);
  const { btcLoading, userDetails, baseUrl, depositHistory, withdrawHistory } = useGlobalContext();
  const storedUser = (() => {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "null");
  } catch {
    return null;
  }
})();

const user = userDetails || storedUser || {};

const { firstName, restricted } = user;

const { profit, bonus, country, symbol } = userDetails || {};

const currencySymbol =
  symbol ||
  country?.symbol ||
  storedUser?.symbol ||
  storedUser?.country?.symbol ||
  "";
  const accountMetrics = [
    { label: "Profits", amount: Number(profit) || 0 },
    { label: "Bonus", amount: Number(bonus) || 0 },
    { label: "Successful deposits", amount: totalForStatuses(depositHistory, ["true", "approved"]) },
    { label: "Successful withdrawals", amount: totalForStatuses(withdrawHistory, ["true", "approved"]) },
    { label: "Pending deposits", amount: totalForStatuses(depositHistory, ["false", "pending"]) },
    { label: "Pending withdrawals", amount: totalForStatuses(withdrawHistory, ["false", "pending"]) },
    { label: "Failed deposits", amount: totalForStatuses(depositHistory, ["failed"]) },
    { label: "Failed withdrawals", amount: totalForStatuses(withdrawHistory, ["failed"]) },
  ];

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadInvestmentActivity = async () => {
      setActivityLoading(true);
      setActivityError("");
      try {
        const response = await getWalletInvestmentPortfolio(baseUrl, {
          signal: controller.signal,
        });
        const portfolio = response.portfolio;
        if (active) {
          setInvestmentActivity(
            portfolio.activity.filter(
              (item) => item.side === "buy" || item.side === "sell"
            )
          );
        }
      } catch (error) {
        if (active && error.name !== "AbortError") {
          setActivityError(
            error.message || "Investment activity could not be loaded."
          );
        }
      } finally {
        if (active) setActivityLoading(false);
      }
    };

    loadInvestmentActivity();
    return () => {
      active = false;
      controller.abort();
    };
  }, [baseUrl, activityRetry]);

  useEffect(() => {
    client
      .fetch('*[_type == "restriction"] { message }')
      .then((data) => setMessage(data))
      .catch(() => {});
  }, []);

  return (
    <section className="wallet_sect wallet-redesign">
      <div className="wr-topbar">
        <DashboardHeader pathname={firstName} />
      </div>
      <main className="wr-content">
        <header className="wr-page-title">
          <div>
            <span className="wr-overline">ACCOUNT OVERVIEW</span>
            <h1>My wallet</h1>
          </div>
        </header>

        <section className="wr-overview" aria-label="Wallet overview and actions">
          <div className="wr-balance-card">
            <div className="wr-balance-orbit orbit-one" />
            <div className="wr-balance-orbit orbit-two" />
            <div className="wr-balance-copy">
              <span className="wr-overline">TOTAL WALLET BALANCE</span>
              <div className="wr-live-balance"><WalletHeader /></div>
            </div>
          </div>
          <nav className="wr-actions" aria-label="Wallet actions">
            {actions.map(({ label, to, icon: Icon, detail }, index) => (
              <Link to={to} key={to} className={"wr-action wr-action-" + (index + 1)}>
                {btcLoading ? (
                  <Skeleton width={48} height={48} style={{ borderRadius: 14 }} />
                ) : (
                  <span className="wr-action-icon"><Icon size={19} /></span>
                )}
                <span className="wr-action-text">
                  {btcLoading ? <Skeleton width={70} height={13} style={{ borderRadius: 20 }} /> : <strong>{label}</strong>}
                  <small>{btcLoading ? <Skeleton width={80} height={10} style={{ borderRadius: 20 }} /> : detail}</small>
                </span>
                <span className="wr-action-arrow" aria-hidden="true">↗</span>
              </Link>
            ))}
          </nav>
        </section>

        <section className="wr-account-summary" aria-labelledby="wr-account-summary-title">
          <div className="wr-section-heading wr-account-summary-heading">
            <div>
              <span className="wr-overline">ACCOUNT SUMMARY</span>
              <h2 id="wr-account-summary-title">Account calculations</h2>
            </div>
          </div>
          <div className="wr-account-metrics">
            {accountMetrics.map(({ label, amount }) => (
              <article className="wr-account-metric" key={label}>
                <span>{label}</span>
                <strong>{formatAccountAmount(amount, currencySymbol)}</strong>
              </article>
            ))}
          </div>
        </section>

        <div className="wr-home-insights">
        <section className="wr-activity-section" aria-labelledby="wr-activity-title">
          <div className="wr-section-heading wr-activity-heading">
            <div>
              <span className="wr-overline">PORTFOLIO ACTIVITY</span>
              <h2 id="wr-activity-title">Recent investment trades</h2>
            </div>
            <Link
              className="wr-activity-all"
              to="/transactions"
              data-testid="link-view-all-investment-transactions"
            >
              View all transactions <FiArrowUpRight size={15} aria-hidden="true" />
            </Link>
          </div>

          {activityLoading ? (
            <div className="wr-activity-loading" role="status" aria-label="Loading investment activity">
              {[0, 1, 2].map((item) => (
                <div className="wr-activity-skeleton" key={item}>
                  <Skeleton circle width={38} height={38} />
                  <div><Skeleton width={120} height={13} /><Skeleton width={170} height={10} /></div>
                  <Skeleton width={75} height={14} />
                </div>
              ))}
            </div>
          ) : activityError ? (
            <div className="wr-activity-state" role="alert">
              <p>{activityError}</p>
              <button type="button" onClick={() => setActivityRetry((count) => count + 1)}>
                Retry
              </button>
            </div>
          ) : investmentActivity.length === 0 ? (
            <div className="wr-activity-state">
              <p>No investment trades yet. Wallet-funded buys and sells will appear here.</p>
              <Link to="/investments">Explore investments</Link>
            </div>
          ) : (
            <ul className="wr-activity-list">
              {investmentActivity.slice(0, 3).map((item, index) => {
                const side = item.side === "buy" ? "buy" : "sell";
                const shares = Number(item.shares) || 0;
                const price = Number(item.priceUsd ?? item.price) || 0;
                const timestamp = item.at || item.createdAt;
                const Icon = side === "buy" ? FiArrowDownRight : FiArrowUpRight;
                const tradeId =
                  item.id || `${item.symbol}-${timestamp || "trade"}-${index}`;

                return (
                  <li
                    className="wr-activity-row"
                    key={tradeId}
                    data-testid={`row-investment-transaction-${tradeId}`}
                  >
                    <span className={`wr-activity-icon is-${side}`} aria-hidden="true">
                      <Icon size={17} />
                    </span>
                    <span className="wr-activity-copy">
                      <strong>{side === "buy" ? "Bought" : "Sold"} {item.symbol}</strong>
                      <small>
                        {investmentShares.format(shares)} shares · {formatInvestmentDate(timestamp)}
                      </small>
                    </span>
                    <span className="wr-activity-value">
                      {investmentMoney.format(shares * price)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>


        </div>

        <footer className="wr-footer"><span>ProfitGrowing</span><span>Market values are shown for reference.</span></footer>
      </main>
      {restricted === "true" && <Modal message={message[0]} />}
    </section>
  );
};

export default Wallet;
